import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDiscreto } from '../../componentes/sensivel';
import { get } from '../../nucleo/api';
import { useSinal, useTempoRealConectado } from '../../nucleo/tempo-real';
import type { Pessoa } from './tipos';

/**
 * Fase 2, W15: o sinal `equipe` do SSE (mensagem, edição, participantes, leitura) recarrega lista, canal e mensagens na
 * hora. Sem SSE, o polling volta com o intervalo dado. Devolve o `refetchInterval` da consulta.
 */
export function useIntervaloEquipe(ms: number): number | false {
  const qc = useQueryClient();
  useSinal('equipe', () => void qc.invalidateQueries({ queryKey: ['equipe'] }));
  return useTempoRealConectado() ? false : ms;
}

/** Total de não lidas nos meus canais (selo do menu e título da lista). */
export function useNaoLidas() {
  const intervalo = useIntervaloEquipe(30_000);
  return useQuery({ queryKey: ['equipe', 'nao-lidas'], queryFn: () => get<{ total: number; canais: number }>('/equipe/nao-lidas'), refetchInterval: intervalo, staleTime: 5_000 });
}

/** Pessoas que usam o chat (para conversa direta, grupo e participantes). */
export function usePessoasDoChat(ativo = true) {
  return useQuery({ queryKey: ['equipe', 'pessoas'], queryFn: () => get<{ itens: Pessoa[] }>('/equipe/pessoas'), enabled: ativo, staleTime: 60_000 });
}

/**
 * Fase 2 (W20): nomes das pessoas que o texto do chat pode citar (as do chat, mais os que a tela já conhece: participantes,
 * autores e mencionados), para o `textoLivre` trocar pelas iniciais no modo discreto com a opção de nomes. Sem a opção, lista
 * vazia (e nem consulta as pessoas).
 */
export function useNomesDoChat(extras: readonly (string | null | undefined)[] = []): string[] {
  const { nomes: ocultos } = useDiscreto();
  const pessoas = usePessoasDoChat(ocultos);
  const chave = extras.filter(Boolean).join('\u0000');
  return useMemo(
    () => (ocultos ? [...new Set([...(pessoas.data?.itens ?? []).map((p) => p.nome), ...chave.split('\u0000')].filter((n) => n.trim()))] : []),
    [ocultos, pessoas.data, chave],
  );
}

export function useDebounce<T>(valor: T, ms = 300) {
  const [d, setD] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setD(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return d;
}

/**
 * Selo do item "Comunicação" no menu principal: não lidas do chat da equipe. Some com zero. No trilho recolhido, fica
 * sobre o ícone; no celular (W17, K11), também no canto do botão do menu. O número tem texto para leitor de tela.
 */
export function SeloNaoLidas({ compacto, noBotao }: { compacto?: boolean; noBotao?: boolean }) {
  const { t } = useTranslation('equipe');
  const q = useNaoLidas();
  const n = q.data?.total ?? 0;
  if (!n) return null;
  return (
    <span
      data-selo-equipe
      className={`numero flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-acento px-1.5 text-xs font-semibold text-acento-tinta ${noBotao ? 'pointer-events-none absolute -top-1 -right-1' : compacto ? 'absolute top-0 right-1' : ''}`}
    >
      <span aria-hidden>{n > 99 ? '99+' : n}</span>
      <span className="sr-only">{t('selo.naoLidas', { count: n })}</span>
    </span>
  );
}
